import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import {
  getQuotation,
  updateQuotation,
  createInvoice,
  deleteQuotation,
  ensureQuotationNumber
} from '../services/firestoreService';
import { generateQuotationPDF } from '../services/pdfService';
import { enrichWorkShifts } from '../utils/helpers';
import WorkShiftsEditor from '../components/quotation/WorkShiftsEditor';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import Loading from '../components/common/Loading';
import {
  formatDate,
  getServiceTypeLabel,
  getStatusBadgeClass,
  getStatusLabel,
  formatCurrency,
  fileToBase64,
  toDateInputValue,
  parseDateInputValue,
  normalizeGuryahaItemName,
  formatItemSelectionLabel,
  mergeGuryahaQuotationItems,
  mergeXafiisyadaQuotationItems
} from '../utils/helpers';
import { HOUSE_TYPES, FLOOR_OPTIONS, BIYAANO_FEATURES, DESTINATION_TYPES } from '../utils/constants';
import Select from '../components/common/Select';
import GuristaGuryahaTable from '../components/quotation/GuristaGuryahaTable';
import GuristaXafiisyadaTable from '../components/quotation/GuristaXafiisyadaTable';
import GuristaGanacsigadaTable from '../components/quotation/GuristaGanacsigadaTable';
import toast from 'react-hot-toast';

const quotationToEditForm = (q) => ({
  customerName: q.customerName || '',
  phone: q.phone || '',
  fromLocation: q.fromLocation || '',
  toLocation: q.toLocation || '',
  movingDate: toDateInputValue(q.movingDate),
  houseType: q.houseType || '',
  floorLevel: q.floorLevel ? String(q.floorLevel) : '',
  biyaanoFeatures: q.biyaanoFeatures || [],
  destinationType: q.destinationType || '',
  destinationFloor: q.destinationFloor ? String(q.destinationFloor) : '',
  dabaqNameFrom: q.dabaqNameFrom || '',
  dabaqNameTo: q.dabaqNameTo || '',
  items: q.items || []
});

const QuotationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { paymentAccounts, employees } = useApp();
  
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editSessionKey, setEditSessionKey] = useState(0);
  const [workShifts, setWorkShifts] = useState([]);
  const [shiftNotes, setShiftNotes] = useState('');
  const [savingShifts, setSavingShifts] = useState(false);
  const [downloadingQuotePdf, setDownloadingQuotePdf] = useState(false);
  
  const [pricing, setPricing] = useState({
    projectAmount: 0,
    xamaaliAmount: 0
  });
  
  const [adminSignature, setAdminSignature] = useState('');
  const [signatureType, setSignatureType] = useState('draw');
  const sigRef = useRef(null);

  useEffect(() => {
    fetchQuotation();
  }, [id]);

  useEffect(() => {
    if (!employees.length) return;
    setWorkShifts((prev) => (prev.length ? enrichWorkShifts(prev, employees) : prev));
  }, [employees]);

  const fetchQuotation = async () => {
    setLoading(true);
    const result = await getQuotation(id);
    if (result.success) {
      let data = result.data;
      if (!data.quotationNumber && id) {
        const numResult = await ensureQuotationNumber(id);
        if (numResult.success && numResult.quotationNumber) {
          data = { ...data, quotationNumber: numResult.quotationNumber };
        }
      }
      setQuotation(data);
      setWorkShifts(enrichWorkShifts(data.workShifts || [], employees));
      setShiftNotes(result.data.shiftNotes || '');
      setPricing({
        projectAmount: result.data.projectAmount || 0,
        xamaaliAmount: result.data.xamaaliAmount || 0
      });
    } else {
      toast.error('Quatation lama helin');
      navigate('/admin/quotations');
    }
    setLoading(false);
  };

  const handlePricingChange = (e) => {
    const { name, value } = e.target;
    setPricing(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        setAdminSignature(base64);
      } catch (error) {
        toast.error('Khalad ayaa dhacay');
      }
    }
  };

  const clearSignature = () => {
    if (sigRef.current) {
      sigRef.current.clear();
    }
    setAdminSignature('');
  };

  const handleCreateInvoice = async () => {
    if (pricing.projectAmount <= 0) {
      toast.error('Fadlan gali qiimaha mashruuca');
      return;
    }
    
    let signature = adminSignature;
    if (!signature && sigRef.current && !sigRef.current.isEmpty()) {
      signature = sigRef.current.toDataURL('image/png');
    }
    
    if (!signature) {
      toast.error('Fadlan saxiix ama soo geli sawirka saxiixa');
      return;
    }

    setSubmitting(true);

    const paymentAccount = paymentAccounts.find(acc => acc.id === quotation.paymentAccountId)
      || paymentAccounts.find(acc => acc.paymentType === quotation.paymentType);

    const result = await createInvoice({
      ...quotation,
      workShifts,
      shiftNotes,
      paymentAccountId: paymentAccount?.id || quotation.paymentAccountId || null
    }, {
      ...pricing,
      paymentAccountId: paymentAccount?.id || null,
      accountNumber: paymentAccount?.accountNumber || '',
      accountName: paymentAccount?.accountName || ''
    }, signature, user ? { uid: user.uid, email: user.email } : null);

    if (result.success) {
      toast.success('Quatation kan waa la sameeyay!');
      navigate(`/admin/invoices/${result.id}`);
    } else {
      toast.error('Khalad: ' + result.error);
    }

    setSubmitting(false);
  };

  const handleReject = async () => {
    setSubmitting(true);
    const result = await updateQuotation(id, { status: 'rejected' });
    if (result.success) {
      toast.success('Quatation kan waa la diiday');
      fetchQuotation();
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const result = await deleteQuotation(id);
    if (result.success) {
      toast.success('Waa la tirtiray');
      navigate('/admin/quotations');
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSubmitting(false);
  };

  const handleSaveShifts = async () => {
    setSavingShifts(true);
    const shiftsToSave = enrichWorkShifts(workShifts, employees);
    const result = await updateQuotation(id, { workShifts: shiftsToSave, shiftNotes });
    if (result.success) {
      toast.success('Shift waa la kaydiyay');
      fetchQuotation();
    } else {
      toast.error(result.error);
    }
    setSavingShifts(false);
  };

  const handleDownloadQuotationPdf = async () => {
    setDownloadingQuotePdf(true);
    try {
      let quoteForPdf = { ...quotation, workShifts: enrichWorkShifts(workShifts, employees), shiftNotes };
      if (!quoteForPdf.quotationNumber && id) {
        const numResult = await ensureQuotationNumber(id);
        if (numResult.success && numResult.quotationNumber) {
          quoteForPdf = { ...quoteForPdf, quotationNumber: numResult.quotationNumber };
          setQuotation((q) => (q ? { ...q, quotationNumber: numResult.quotationNumber } : q));
        }
      }
      await generateQuotationPDF({ ...quoteForPdf, pdfEmployees: employees });
      toast.success('PDF waa la soo dejiyay');
    } catch (e) {
      toast.error('PDF khalad');
    }
    setDownloadingQuotePdf(false);
  };

  const startEditing = () => {
    setEditForm(quotationToEditForm(quotation));
    setEditSessionKey((k) => k + 1);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm(null);
  };

  const handleEditFieldChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'houseType') {
        next.floorLevel = '';
        next.biyaanoFeatures = [];
        next.destinationType = '';
        next.destinationFloor = '';
        next.dabaqNameFrom = '';
        next.dabaqNameTo = '';
      }
      if (name === 'floorLevel') {
        next.destinationType = '';
        next.destinationFloor = '';
        next.dabaqNameTo = '';
      }
      if (name === 'destinationType' && value !== 'dabaq') {
        next.destinationFloor = '';
        next.dabaqNameTo = '';
        next.biyaanoFeatures = [];
      }
      return next;
    });
  };

  const handleEditFeatureChange = (featureValue) => {
    setEditForm((prev) => {
      const current = prev.biyaanoFeatures || [];
      const biyaanoFeatures = current.includes(featureValue)
        ? current.filter((f) => f !== featureValue)
        : [...current, featureValue];
      return { ...prev, biyaanoFeatures };
    });
  };

  const handleEditItemsChange = (items) => {
    setEditForm((prev) => ({ ...prev, items }));
  };

  const handleSaveEdit = async () => {
    if (!editForm.customerName.trim() || !editForm.phone.trim()) {
      toast.error('Magaca iyo telefoonka waa lagama maarmaan');
      return;
    }
    if (!editForm.fromLocation.trim() || !editForm.toLocation.trim()) {
      toast.error('Goobaha waa lagama maarmaan');
      return;
    }
    if (!editForm.movingDate) {
      toast.error('Taariikhda guuritaanka waa lagama maarmaan');
      return;
    }

    setSavingEdit(true);
    const result = await updateQuotation(id, {
      customerName: editForm.customerName.trim(),
      phone: editForm.phone.trim(),
      fromLocation: editForm.fromLocation.trim(),
      toLocation: editForm.toLocation.trim(),
      movingDate: parseDateInputValue(editForm.movingDate),
      houseType: editForm.houseType || null,
      floorLevel: editForm.floorLevel || null,
      biyaanoFeatures: editForm.biyaanoFeatures || [],
      destinationType: editForm.destinationType || null,
      destinationFloor: editForm.destinationFloor || null,
      dabaqNameFrom: editForm.dabaqNameFrom?.trim() || '',
      dabaqNameTo: editForm.dabaqNameTo?.trim() || '',
      items: editForm.items || []
    });

    if (result.success) {
      toast.success('Quatation waa la cusboonaysiiyay');
      setIsEditing(false);
      setEditForm(null);
      fetchQuotation();
    } else {
      toast.error('Khalad: ' + result.error);
    }
    setSavingEdit(false);
  };

  const renderEditableItems = () => {
    if (!editForm) return null;
    const tableKey = `${quotation.serviceType}-${editSessionKey}`;

    if (quotation.serviceType === 'gurista-guryaha') {
      return (
        <GuristaGuryahaTable
          key={tableKey}
          fullCatalog
          items={editForm.items}
          onChange={handleEditItemsChange}
        />
      );
    }
    if (quotation.serviceType === 'gurista-xafiisyada') {
      return (
        <GuristaXafiisyadaTable
          key={tableKey}
          fullCatalog
          items={editForm.items}
          onChange={handleEditItemsChange}
        />
      );
    }
    if (quotation.serviceType === 'gurista-ganacsiyada') {
      return (
        <GuristaGanacsigadaTable
          key={tableKey}
          items={editForm.items}
          onChange={handleEditItemsChange}
        />
      );
    }
    return null;
  };

  // ✅ Get house type label
  const getHouseTypeLabel = (type) => {
    const found = HOUSE_TYPES.find(h => h.value === type);
    return found ? found.label : type;
  };

  // ✅ Get floor label
  const getFloorLabel = (level) => {
    if (level === null || level === undefined || level === '') return '';
    const value = String(level);
    const found = FLOOR_OPTIONS.find(f => f.value === value);
    return found ? found.label : `Biyaanada ${value}aad`;
  };

  // ✅ Get feature labels
  const getFeatureLabels = (features) => {
    if (!features || !Array.isArray(features)) return [];
    return features.map(f => {
      const found = BIYAANO_FEATURES.find(bf => bf.value === f);
      return found ? found.label : f;
    });
  };

  // ✅ Get destination label
  const getDestinationLabel = (type) => {
    const found = DESTINATION_TYPES.find(d => d.value === type);
    return found ? found.label : type;
  };

  const itemTableHead = 'bg-[#C62828] text-white';
  const itemNameCell = 'font-semibold text-[#C62828]';
  const serviceTypeTitleClass = 'font-semibold text-[#C62828]';

  // Render items based on service type
  const renderItems = () => {
    if (!quotation.items || quotation.items.length === 0) {
      return <p className="text-gray-500">Alaabo lama dooran</p>;
    }

    if (quotation.serviceType === 'gurista-guryaha') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className={itemTableHead}>
              <tr>
                <th className="px-3 py-2 text-left">Alaabta</th>
                <th className="px-3 py-2 text-left">Nooca</th>
                <th className="px-3 py-2 text-center">Dhaawac</th>
                <th className="px-3 py-2 text-center">Tirada</th>
                <th className="px-3 py-2 text-left">Faallo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-900">
              {mergeGuryahaQuotationItems(quotation.items).map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className={`px-3 py-2 ${itemNameCell}`}>{normalizeGuryahaItemName(item.itemName)}</td>
                  <td className="px-3 py-2">
                    {item.selection ? (
                      <span className="px-2 py-1 bg-slate-100 text-gray-900 rounded text-xs">
                        {formatItemSelectionLabel(item.selection)}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {item.damaged ? <span className="text-red-500">⚠️ Haa</span> : '-'}
                  </td>
                  <td className="px-3 py-2 text-center font-medium">{item.quantity || 0}</td>
                  <td className="px-3 py-2 text-gray-700">{item.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (quotation.serviceType === 'gurista-xafiisyada') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={itemTableHead}>
              <tr>
                <th className="px-4 py-2 text-left text-sm">Alaabta</th>
                <th className="px-4 py-2 text-center text-sm">Dhaawacsan</th>
                <th className="px-4 py-2 text-center text-sm">Tirada</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-900">
              {mergeXafiisyadaQuotationItems(quotation.items).map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className={`px-4 py-2 ${itemNameCell}`}>{item.itemName}</td>
                  <td className="px-4 py-2 text-center">
                    {item.damaged ? <span className="text-red-500">⚠️ Haa</span> : '-'}
                  </td>
                  <td className="px-4 py-2 text-center font-medium">{item.quantity || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (quotation.serviceType === 'gurista-ganacsiyada') {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={itemTableHead}>
              <tr>
                <th className="px-4 py-2 text-left text-sm">Sharaxaadda</th>
                <th className="px-4 py-2 text-center text-sm">Tirada</th>
                <th className="px-4 py-2 text-left text-sm">Fahfahin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-gray-900">
              {quotation.items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className={`px-4 py-2 ${itemNameCell}`}>{item.description}</td>
                  <td className="px-4 py-2 text-center font-medium">{item.quantity || 0}</td>
                  <td className="px-4 py-2 text-gray-700">{item.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (!quotation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Quatation lama helin</p>
      </div>
    );
  }

  const totalAmount = pricing.projectAmount + pricing.xamaaliAmount;
  const paymentAccount = paymentAccounts.find(acc => acc.id === quotation.paymentAccountId);
  return (
    <div className="space-y-6">
      {isEditing && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-medium text-amber-900">
            Wax ka beddel — kaydi markaad dhammayso si invoice/PDF uu u ekaado cusboonaysiinta.
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" onClick={cancelEditing} disabled={savingEdit}>
              Jooji
            </Button>
            <Button onClick={handleSaveEdit} loading={savingEdit}>
              Kaydi
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <button
            onClick={() => navigate('/admin/quotations')}
            className="text-gray-500 hover:text-gray-700 mb-2 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Dib u noqo
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Faahfaahinta Quatation ka</h1>
          {quotation.quotationNumber && (
            <p className="text-primary-600 font-mono font-semibold mt-1">#{quotation.quotationNumber}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(quotation.status)}`}>
            {getStatusLabel(quotation.status)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card title="Macluumaadka Macmiilka" icon={<span className="text-xl">👤</span>}>
            {isEditing && editForm ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Magaca" name="customerName" value={editForm.customerName} onChange={handleEditFieldChange} required />
                <Input label="Telefoon" name="phone" value={editForm.phone} onChange={handleEditFieldChange} required />
                <Input label="Ka soco" name="fromLocation" value={editForm.fromLocation} onChange={handleEditFieldChange} required />
                <Input label="Ku socdo" name="toLocation" value={editForm.toLocation} onChange={handleEditFieldChange} required />
                <Input label="Taariikhda Guuritaanka" name="movingDate" type="date" value={editForm.movingDate} onChange={handleEditFieldChange} required />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Nooca Adeegga</p>
                  <p className={serviceTypeTitleClass}>{getServiceTypeLabel(quotation.serviceType)}</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Magaca</p>
                  <p className="font-medium text-gray-900">{quotation.customerName}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Telefoon</p>
                  <p className="font-medium text-gray-900">{quotation.phone}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Ka soco</p>
                  <p className="font-medium text-gray-900">{quotation.fromLocation}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Ku socdo</p>
                  <p className="font-medium text-gray-900">{quotation.toLocation}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Taariikhda Guuritaanka</p>
                  <p className="font-medium text-gray-900">{formatDate(quotation.movingDate)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0D47A1]">Nooca Adeegga</p>
                  <p className={serviceTypeTitleClass}>{getServiceTypeLabel(quotation.serviceType)}</p>
                </div>
              </div>
            )}
          </Card>

          {/* House Type + Destination (Dabaq / Biyaano) */}
          {(isEditing || quotation.houseType || quotation.destinationType) && (
            <Card title="Nooca Guriga" icon={<span className="text-xl">🏠</span>}>
              {isEditing && editForm ? (
                <div className="space-y-4">
                  <Select
                    label="Nooca Guriga"
                    name="houseType"
                    value={editForm.houseType}
                    onChange={handleEditFieldChange}
                    options={HOUSE_TYPES}
                  />
                  {editForm.houseType === 'dabaq' && (
                    <>
                      <Select
                        label="Biyaanada (Ka gurayso)"
                        name="floorLevel"
                        value={editForm.floorLevel}
                        onChange={handleEditFieldChange}
                        options={FLOOR_OPTIONS}
                      />
                      <Input
                        label="Magaca dabaqka / biyaaanka (ka gurayso)"
                        name="dabaqNameFrom"
                        value={editForm.dabaqNameFrom}
                        onChange={handleEditFieldChange}
                        placeholder="Magaca dabaqka ama biyaaanka..."
                      />
                    </>
                  )}
                  {(editForm.houseType === 'guri-caadi' || editForm.floorLevel) && (
                    <Select
                      label="U gurayso"
                      name="destinationType"
                      value={editForm.destinationType}
                      onChange={handleEditFieldChange}
                      options={DESTINATION_TYPES}
                    />
                  )}
                  {editForm.destinationType === 'dabaq' && (
                    <>
                      <Select
                        label="Biyaanada (U gurayso)"
                        name="destinationFloor"
                        value={editForm.destinationFloor}
                        onChange={handleEditFieldChange}
                        options={
                          editForm.floorLevel
                            ? FLOOR_OPTIONS.filter((f) => parseInt(f.value, 10) >= parseInt(editForm.floorLevel, 10))
                            : FLOOR_OPTIONS
                        }
                      />
                      <Input
                        label="Magaca dabaqka / biyaaanka (u gurayso)"
                        name="dabaqNameTo"
                        value={editForm.dabaqNameTo}
                        onChange={handleEditFieldChange}
                        placeholder="Magaca dabaqka ama biyaaanka..."
                      />
                    </>
                  )}
                  {(editForm.houseType === 'dabaq' || editForm.destinationType === 'dabaq') && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Nooca Biyaanada</p>
                      <div className="flex flex-wrap gap-3">
                        {BIYAANO_FEATURES.map((feature) => (
                          <label key={feature.value} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={editForm.biyaanoFeatures?.includes(feature.value) || false}
                              onChange={() => handleEditFeatureChange(feature.value)}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            {feature.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quotation.houseType && (
                  <div>
                    <p className="text-sm text-gray-500">Ka gurayso (Nooca)</p>
                    <p className="font-medium mt-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        quotation.houseType === 'dabaq'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {quotation.houseType === 'dabaq' ? '🏢' : '🏠'} {getHouseTypeLabel(quotation.houseType)}
                      </span>
                    </p>
                  </div>
                )}

                {quotation.houseType === 'dabaq' && quotation.floorLevel && (
                  <div>
                    <p className="text-sm text-gray-500">Biyaanada (Ka gurayso)</p>
                    <p className="font-medium mt-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                        🔢 {getFloorLabel(quotation.floorLevel)}
                      </span>
                    </p>
                    {quotation.dabaqNameFrom && (
                      <p className="text-sm text-gray-700 mt-1">Magac: {quotation.dabaqNameFrom}</p>
                    )}
                  </div>
                )}

                {quotation.destinationType && (
                  <div>
                    <p className="text-sm text-gray-500">U gurayso (Xaggee?)</p>
                    <p className="font-medium mt-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        quotation.destinationType === 'dabaq'
                          ? 'bg-indigo-100 text-indigo-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {quotation.destinationType === 'dabaq' ? '🏢' : '🏠'} {getDestinationLabel(quotation.destinationType)}
                      </span>
                    </p>
                  </div>
                )}

                {quotation.destinationType === 'dabaq' && quotation.destinationFloor && (
                  <div>
                    <p className="text-sm text-gray-500">Biyaanada (U gurayso)</p>
                    <p className="font-medium mt-1">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
                        🔢 {getFloorLabel(quotation.destinationFloor)}
                      </span>
                    </p>
                    {quotation.dabaqNameTo && (
                      <p className="text-sm text-gray-700 mt-1">Magac: {quotation.dabaqNameTo}</p>
                    )}
                  </div>
                )}
              </div>

              {quotation.biyaanoFeatures && quotation.biyaanoFeatures.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500">Nooca Biyaanada</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {getFeatureLabels(quotation.biyaanoFeatures).map((label, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Faahfaahin:</span>{' '}
                  {getHouseTypeLabel(quotation.houseType)}
                  {quotation.houseType === 'dabaq' && quotation.floorLevel && (
                    <>, Ka gurayso: {getFloorLabel(quotation.floorLevel)}</>
                  )}
                  {quotation.destinationType && (
                    <>
                      {' → '}
                      {quotation.destinationType === 'guri-caadi'
                        ? 'Guri Caadi ah'
                        : quotation.destinationFloor
                          ? `U gurayso: ${getFloorLabel(quotation.destinationFloor)}`
                          : 'Dabaq / Biyaano'
                      }
                    </>
                  )}
                  {quotation.biyaanoFeatures && quotation.biyaanoFeatures.length > 0 && (
                    <> ({getFeatureLabels(quotation.biyaanoFeatures).join(', ')})</>
                  )}
                </p>
              </div>
              </>
              )}
            </Card>
          )}

          <Card title="Shift-ka shaqada" icon={<span className="text-xl">🕐</span>}>
            <p className="text-sm text-blue-700 mb-3">
              Shift, supervisor, Xamaali, packaging, iyo baabuur — mid kasta gooni.
            </p>
            <WorkShiftsEditor
              shifts={workShifts}
              employees={employees}
              onChange={setWorkShifts}
              readOnly={quotation.status !== 'pending'}
              defaultDate={toDateInputValue(quotation.movingDate)}
              brandBlue
            />
            {quotation.status === 'pending' && (
              <>
                <Input
                  label="Faallo shift"
                  value={shiftNotes}
                  onChange={(e) => setShiftNotes(e.target.value)}
                  className="mt-4"
                />
                <Button className="mt-3" onClick={handleSaveShifts} loading={savingShifts}>
                  Kaydi shift
                </Button>
              </>
            )}
            {quotation.status !== 'pending' && shiftNotes && (
              <p className="text-sm text-gray-600 mt-2">{shiftNotes}</p>
            )}
          </Card>

          {/* Items */}
          <Card title="Alaabada la Doortay" icon={<span className="text-xl">📦</span>}>
            <p className={`text-sm font-semibold text-[#C62828] -mt-1 mb-4`}>
              {getServiceTypeLabel(quotation.serviceType)}
            </p>
            {isEditing ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Hagaaji tirada, ka saar alaab (tirada 0), ama ku dar alaab cusub.
                </p>
                {renderEditableItems()}
              </>
            ) : (
              renderItems()
            )}
          </Card>

          {/* Payment Info */}
          <Card title="Macluumaadka Lacag Bixinta" icon={<span className="text-xl">💳</span>}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Nooca Lacagta</p>
                <p className="font-medium">{quotation.paymentType}</p>
              </div>
              {paymentAccount && (
                <>
                  <div>
                    <p className="text-sm text-gray-500">Akoonka</p>
                    <p className="font-medium">{paymentAccount.accountNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Magaca Akoonka</p>
                    <p className="font-medium">{paymentAccount.accountName}</p>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Customer Signature */}
          <Card title="Saxiixa Macmiilka" icon={<span className="text-xl">✍️</span>}>
            {quotation.customerSignature ? (
              <div className="border rounded-lg p-4 bg-gray-50">
                <img 
                  src={quotation.customerSignature} 
                  alt="Customer Signature" 
                  className="max-h-32 mx-auto"
                />
              </div>
            ) : (
              <p className="text-gray-500">Saxiix lama helin</p>
            )}
          </Card>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6">
          {/* Dates */}
          <Card>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">La sameeyay</p>
                <p className="font-medium">{formatDate(quotation.createdAt, 'dd/MM/yyyy HH:mm')}</p>
              </div>
              {quotation.approvedAt && (
                <div>
                  <p className="text-sm text-gray-500">La ansixiyay</p>
                  <p className="font-medium">{formatDate(quotation.approvedAt, 'dd/MM/yyyy HH:mm')}</p>
                </div>
              )}
              {quotation.updatedAt && (
                <div>
                  <p className="text-sm text-gray-500">La cusboonaysiiyay</p>
                  <p className="font-medium">{formatDate(quotation.updatedAt, 'dd/MM/yyyy HH:mm')}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Actions for Pending Quotations */}
          <Card title="PDF">
            <Button fullWidth variant="outline" onClick={handleDownloadQuotationPdf} loading={downloadingQuotePdf}>
              Soo deji Quotation PDF (A4)
            </Button>
          </Card>

          {quotation.status === 'pending' && (
            <Card title="Tallaabooyin">
              <div className="space-y-3">
                {!isEditing ? (
                  <Button
                    fullWidth
                    variant="outline"
                    onClick={startEditing}
                    icon={<span>✏️</span>}
                  >
                    Wax ka beddel
                  </Button>
                ) : (
                  <Button fullWidth onClick={handleSaveEdit} loading={savingEdit} icon={<span>💾</span>}>
                    Kaydi isbeddelada
                  </Button>
                )}
                <Button
                  fullWidth
                  onClick={() => setShowPricingModal(true)}
                  icon={<span>💰</span>}
                  disabled={isEditing}
                >
                  Qiimee & Ansixid
                </Button>
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handleReject}
                  loading={submitting}
                >
                  Diid
                </Button>
                <Button
                  fullWidth
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Tirtir
                </Button>
              </div>
            </Card>
          )}

          {/* If already invoiced */}
          {quotation.status === 'invoiced' && quotation.invoiceId && (
            <Card title="Invoice yada">
              <div className="text-center">
                <p className="text-green-600 font-medium mb-3">✅ Invoice ka waa la sameeyay</p>
                <Button
                  fullWidth
                  onClick={() => navigate(`/admin/invoices/${quotation.invoiceId}`)}
                >
                  Arag Invoice yada
                </Button>
              </div>
            </Card>
          )}

          {/* Pricing Summary if set */}
          {(quotation.projectAmount > 0 || quotation.xamaaliAmount > 0) && (
            <Card title="Qiimaha">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Mashruuca:</span>
                  <span className="font-medium">{formatCurrency(quotation.projectAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Xamaali:</span>
                  <span className="font-medium">{formatCurrency(quotation.xamaaliAmount)}</span>
                </div>
                <hr />
                <div className="flex justify-between text-lg">
                  <span className="font-bold">Wadarta:</span>
                  <span className="font-bold text-red-600">
                    {formatCurrency(quotation.totalAmount)}
                  </span>
                </div>
              </div>
            </Card>
          )}

          {/* Delete button for non-pending quotations */}
          {quotation.status !== 'pending' && (
            <Card>
              <Button
                fullWidth
                variant="danger"
                onClick={() => setShowDeleteModal(true)}
                icon={<span>🗑️</span>}
              >
                Tirtir Quatation
              </Button>
            </Card>
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      <Modal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        title="Qiimee Qoraashida"
        size="lg"
      >
        <div className="space-y-6">
          {/* Pricing Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Qiimaha Mashruuca ($)"
              name="projectAmount"
              type="number"
              min="0"
              step="0.01"
              value={pricing.projectAmount}
              onChange={handlePricingChange}
              required
            />
          </div>

          {/* Total */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-center text-xl">
              <span className="font-bold">Wadarta Guud:</span>
              <span className="font-bold text-red-600">{formatCurrency(totalAmount)}</span>
            </div>
          </div>

          {/* Admin Signature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Saxiixa Mamulka-ka <span className="text-red-500">*</span>
            </label>
            
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setSignatureType('draw')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  signatureType === 'draw' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ✍️ Gacanta ku saxiix
              </button>
              <button
                type="button"
                onClick={() => setSignatureType('upload')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  signatureType === 'upload' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                📤 Soo geli sawir
              </button>
            </div>

            {signatureType === 'draw' ? (
              <div>
                <div className="border-2 border-dashed border-red-300 rounded-lg bg-gray-50">
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="#C62828"
                    canvasProps={{
                      width: 400,
                      height: 150,
                      className: 'w-full rounded-lg'
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  🗑️ Tirtir saxiixa
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {adminSignature && (
                  <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                    <img 
                      src={adminSignature} 
                      alt="Admin Signature" 
                      className="max-h-24 mx-auto"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => setShowPricingModal(false)}
              fullWidth
            >
              Ka noqo
            </Button>
            <Button
              onClick={handleCreateInvoice}
              loading={submitting}
              fullWidth
            >
              Samee Invoice
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Ma hubtaa?"
        size="sm"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-600 mb-6">
            Ma hubtaa inaad rabto inaad tirtirto quatation kan?
          </p>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              fullWidth
            >
              Maya
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={submitting}
              fullWidth
            >
              Haa, Tirtir
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QuotationDetail;